/**
 * Slide-out panel for inviting new team members.
 * Uses portal pattern like AuthModal.
 * Includes email input, role selection, and success/error states.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../common/ToastProvider';
import { inviteMember } from '../../services/teamService';
import RoleSelector from './RoleSelector';
import Input from '../base/Input';
import Button from '../base/Button';
import type { TeamRole } from '../../types/company';

type InviteRole = Exclude<TeamRole, 'OWNER'>;

interface TeamInvitePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type PanelState = 'idle' | 'loading' | 'success' | 'error';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Error code mapping. Keys are looked up against the `team` namespace
// (useTranslation('team') below) so they're stored without a `team.`
// prefix — the flattened translation files require single-level keys.
const ERROR_CODES: Record<string, string> = {
  ALREADY_MEMBER: 'alreadyMember',
  ALREADY_INVITED: 'alreadyInvited',
  LIMIT_REACHED: 'limitReached',
};

export default function TeamInvitePanel({
  isOpen,
  onClose,
  onSuccess,
}: TeamInvitePanelProps) {
  const { t, i18n } = useTranslation('team');
  const navigate = useNavigate();
  const { session } = useAuth();
  const { showToast } = useToast();

  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<InviteRole | null>(null);
  const [panelState, setPanelState] = useState<PanelState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successEmail, setSuccessEmail] = useState('');

  // Reset form when panel opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setSelectedRole(null);
      setPanelState('idle');
      setErrorMessage('');
      setEmailError('');
      setSuccessEmail('');
    }
  }, [isOpen]);

  // Lock body scroll when panel is open
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Validate email
  const validateEmail = useCallback((value: string): boolean => {
    if (!value.trim()) {
      setEmailError(t('common:auth.emailRequired', 'Email is required'));
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError(t('common:auth.emailInvalid', 'Invalid email address'));
      return false;
    }
    setEmailError('');
    return true;
  }, [t]);

  // Handle email change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!validateEmail(email)) return;
    if (!selectedRole) return;
    if (!session?.token) return;

    setPanelState('loading');
    setErrorMessage('');

    try {
      await inviteMember(session.token, email.trim(), selectedRole);

      // Success
      setSuccessEmail(email.trim());
      setPanelState('success');
      showToast(t('inviteSent', { email: email.trim() }), 'success');
      onSuccess(); // Trigger data refetch
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';

      // Check for subscription required redirect
      if (message.includes('SUBSCRIPTION_REQUIRED') || message.includes('Subscribe to a plan')) {
        showToast(t('subscriptionRequired', 'Subscribe to invite more members'), 'warning');
        onClose();
        // Defer navigation to let panel close and avoid UI freeze
        setTimeout(() => navigate('/membership?reason=required'), 150);
        return;
      }

      setPanelState('error');

      // Check for known error codes
      let errorKey = '';
      for (const [code, key] of Object.entries(ERROR_CODES)) {
        if (message.includes(code) || message.toLowerCase().includes(code.toLowerCase())) {
          errorKey = key;
          break;
        }
      }

      if (errorKey) {
        setErrorMessage(t(errorKey));
      } else if (message.includes('limit') || message.includes('LIMIT')) {
        // Handle limit reached with server message
        setErrorMessage(message);
      } else {
        setErrorMessage(t('inviteFailed', 'Failed to send invitation'));
      }
    }
  };

  // Reset form for "Invite Another"
  const handleInviteAnother = () => {
    setEmail('');
    setSelectedRole(null);
    setPanelState('idle');
    setErrorMessage('');
    setEmailError('');
    setSuccessEmail('');
  };

  // Check if submit should be disabled
  const isSubmitDisabled = panelState === 'loading' || !email.trim() || !selectedRole;

  if (!isOpen) return null;

  const isRtl = i18n.dir() === 'rtl';

  const panelContent = (
    <div className="fixed inset-0 z-[120] flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`
          absolute top-0 bottom-0 w-full max-w-md bg-white shadow-2xl
          transform transition-transform duration-300 ease-out
          flex flex-col
          ${isRtl ? 'left-0' : 'right-0'}
          ${isOpen ? 'translate-x-0' : isRtl ? '-translate-x-full' : 'translate-x-full'}
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-panel-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="invite-panel-title" className="text-xl font-semibold text-[var(--pv-color-primary)]">
            {t('inviteMember', 'Invite Team Member')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={t('common:close', 'Close')}
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {panelState === 'success' ? (
            // Success State
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-check-line text-3xl text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('common:success', 'Success!')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('inviteSent', { email: successEmail })}
              </p>
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <Button onClick={handleInviteAnother} variant="outline">
                  <i className="ri-user-add-line mr-2" />
                  {t('inviteAnother', 'Invite Another')}
                </Button>
                <Button onClick={onClose} variant="ghost">
                  {t('common:done', 'Done')}
                </Button>
              </div>
            </div>
          ) : (
            // Form State
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {errorMessage && panelState === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-2" />
                  {errorMessage}
                </div>
              )}

              {/* Email Input */}
              <div>
                <label
                  htmlFor="invite-email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t('emailLabel', 'Email Address')}
                </label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => email && validateEmail(email)}
                  placeholder={t('emailPlaceholder', 'colleague@company.com')}
                  disabled={panelState === 'loading'}
                  className={emailError ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}
                  autoFocus
                />
                {emailError && (
                  <p className="mt-1.5 text-sm text-red-600">{emailError}</p>
                )}
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('selectRole', 'Select Role')}
                </label>
                <RoleSelector
                  selectedRole={selectedRole}
                  onChange={setSelectedRole}
                  disabled={panelState === 'loading'}
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitDisabled}
                loading={panelState === 'loading'}
                className="w-full"
              >
                <i className="ri-send-plane-line mr-2" />
                {t('sendInvitation', 'Send Invitation')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(panelContent, document.body);
  }

  return panelContent;
}
